// Activity export in TCX format (importable into Strava, Garmin Connect, intervals.icu…).
// Shared by the client's download and the API's upload to Strava.
import type { Samples, Summary } from './stats.js';
import { TRACKS, trackAt, type Track, type TrackDef } from './track.js';
import { challengeForTrack } from './challenges.js';

const XML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' };
/** The name comes from the player: it must not be able to break the document. */
export const xmlEscape = (s: string): string => s.replace(/[&<>"']/g, c => XML_ESCAPES[c]);

export interface TcxActivity { name: string; summary: Pick<Summary, 'duration' | 'distance' | 'kJ'> }

/**
 * Where the game's world sits on the globe in an exported file: Point Nemo, the ocean point farthest from any land. Like
 * Zwift's islands, this draws the ride on a map, and in open water it can never match a real road, segment or person.
 */
export const VIRTUAL_ORIGIN = { lat: -48.88, lon: -123.39 } as const;
const EARTH_RADIUS_M = 6_371_000;

/** Latitude and longitude of a distance along a track, in the virtual world. The 3D scene's +z points south. */
export function virtualPosition(tr: Track, s: number): { lat: number; lon: number } {
  const p = trackAt(tr, s), rad = (VIRTUAL_ORIGIN.lat * Math.PI) / 180;
  return { lat: VIRTUAL_ORIGIN.lat - ((p.z / EARTH_RADIUS_M) * 180) / Math.PI, lon: VIRTUAL_ORIGIN.lon + ((p.x / (EARTH_RADIUS_M * Math.cos(rad))) * 180) / Math.PI };
}

/** Official or legendary track behind an activity's track id; null for a community creation or an unknown id. */
export const officialTrackDef = (id: string | null | undefined): TrackDef | null => (id ? (TRACKS[id] ?? challengeForTrack(id)?.track ?? null) : null);

/**
 * @param track where the ride happened: its trackpoints then carry virtual coordinates, which is what draws the map.
 * @param end end date of the activity (ms): the recording is dated at its end, the start is derived from it.
 */
export function toTCX(act: TcxActivity, S: Samples, end: number, track: Track | null = null): string {
  const t0 = end - (S.t.length ? S.t[S.t.length - 1] * 1000 : 0);
  const iso = (ms: number) => new Date(ms).toISOString();
  const position = (i: number) => {
    if (!track) return '';
    const g = virtualPosition(track, S.d[i] || 0);
    return `\n          <Position><LatitudeDegrees>${g.lat.toFixed(6)}</LatitudeDegrees><LongitudeDegrees>${g.lon.toFixed(6)}</LongitudeDegrees></Position>`;
  };
  const pts = S.t.map((t, i) => `        <Trackpoint>
          <Time>${iso(t0 + t * 1000)}</Time>${position(i)}
          <AltitudeMeters>${(S.alt[i] || 0).toFixed(1)}</AltitudeMeters>
          <DistanceMeters>${(S.d[i] || 0).toFixed(1)}</DistanceMeters>${S.hr[i] ? `\n          <HeartRateBpm><Value>${Math.round(S.hr[i])}</Value></HeartRateBpm>` : ''}
          <Cadence>${Math.round(S.c[i] || 0)}</Cadence>
          <Extensions><ns3:TPX><ns3:Speed>${(S.v[i] || 0).toFixed(2)}</ns3:Speed><ns3:Watts>${Math.round(S.p[i] || 0)}</ns3:Watts></ns3:TPX></Extensions>
        </Trackpoint>`).join('\n');
  const s = act.summary;
  return `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2" xmlns:ns3="http://www.garmin.com/xmlschemas/ActivityExtension/v2">
  <Activities>
    <Activity Sport="Biking">
      <Id>${iso(t0)}</Id>
      <Lap StartTime="${iso(t0)}">
        <TotalTimeSeconds>${s.duration}</TotalTimeSeconds>
        <DistanceMeters>${s.distance.toFixed(1)}</DistanceMeters>
        <Calories>${Math.round(s.kJ * 1.05)}</Calories>
        <Intensity>Active</Intensity>
        <TriggerMethod>Manual</TriggerMethod>
        <Track>
${pts}
        </Track>
      </Lap>
      <Notes>${xmlEscape(act.name)} (Gruvetto)</Notes>
    </Activity>
  </Activities>
</TrainingCenterDatabase>
`;
}
